import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BOOKS, AI_BOOKS, BUY_LINKS, COUNTRIES } from "../data/content";
import useLenis from "../hooks/useLenis";
import useCountry from "../hooks/useCountry";
import CountryPicker from "../components/site/CountryPicker";
import BuyDropdown from "../components/site/BuyDropdown";

const REASONS = [
  {
    n: "01",
    title: "A gift that grows with the child.",
    body: "These aren't disposable stories. The same 26-page picture book keeps giving — first as a bedtime read, later as an early-reader activity book, later still as a conversation starter over dinner.",
  },
  {
    n: "02",
    title: "Story on one side, activity on the other.",
    body: "Every book pairs Maya's story with playful practice — sorting coins, spotting needs vs wants, checking what an AI answer got wrong. Real skills, quietly built.",
  },
  {
    n: "03",
    title: "Big ideas, gentle voices.",
    body: "Money. Banks. Artificial intelligence. The books your child will meet everywhere — introduced kindly, at their pace, from the inside out.",
  },
  {
    n: "04",
    title: "Beautifully made.",
    body: "Finance for Little Hands is available in paperback and Kindle eBook editions; AI for Little Hands is available in paperback. Every edition is illustrated with care and made to be re-read.",
  },
];

const BUNDLES = [
  {
    id: "finance",
    label: "The Money Starter Set",
    tag: "Perfect first bookshelf",
    color: "#FBBF24",
    books: BOOKS,
    pitch:
      "All three Finance for Little Hands books — money, banks, and spending — in one thoughtful gift. Available in paperback and Kindle eBook editions.",
  },
  {
    id: "ai",
    label: "The AI Starter Set",
    tag: "For the curious little mind",
    color: "#5EEAD4",
    books: AI_BOOKS,
    pitch:
      "The full AI for Little Hands paperback trilogy — computers, everyday AI, and safe & smart choices.",
  },
];

function BuyBadge({ book, country }) {
  const url = country ? BUY_LINKS[book.id]?.[country] : null;
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-buy !py-2 !px-4 !text-[11px]"
        data-testid={`gift-buy-${book.id}`}
      >
        Buy on Amazon <span>↗</span>
      </a>
    );
  }
  return <BuyDropdown book={book} />;
}

function BundleCard({ bundle, country, delay = 0 }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay }}
      className="col-span-12 md:col-span-6 border border-stone-900/15 bg-[color:var(--paper)]"
      data-testid={`gift-bundle-${bundle.id}`}
      style={{
        boxShadow: "0 20px 60px rgba(28,25,23,0.06)",
      }}
    >
      <div
        className="aspect-[16/10] flex items-center justify-center relative overflow-hidden"
        style={{
          background: `radial-gradient(120% 80% at 50% 20%, ${bundle.color}33, transparent 60%), var(--paper-warm)`,
        }}
      >
        <div className="flex items-end gap-3">
          {bundle.books.map((b, i) => (
            <motion.img
              key={b.id}
              src={b.cover}
              alt={b.title}
              className="w-[28%] rounded-[4px] shadow-[0_12px_30px_rgba(28,25,23,0.18)]"
              style={{ transform: `rotate(${(i - 1) * 5}deg)` }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: delay + i * 0.08 }}
            />
          ))}
        </div>
        <span className="folio-num absolute top-4 left-4">{bundle.tag}</span>
      </div>

      <div className="p-6 md:p-8">
        <h3 className="font-serif text-3xl md:text-4xl leading-tight">
          {bundle.label}
        </h3>
        <p className="mt-3 text-stone-700 leading-relaxed">{bundle.pitch}</p>

        <ul className="mt-6 divide-y divide-stone-900/10 border-t border-stone-900/10">
          {bundle.books.map((b) => (
            <li
              key={b.id}
              className="py-3 flex items-center gap-4"
              data-testid={`gift-book-${b.id}`}
            >
              <img
                src={b.cover}
                alt={b.title}
                className="w-11 h-11 object-cover rounded-[3px] border border-stone-900/10 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight truncate">{b.title}</p>
                <p className="text-[11px] text-stone-500 truncate">{b.subtitle}</p>
              </div>
              <BuyBadge book={b} country={country} />
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function GiftGuidePage() {
  useLenis();
  const { country, meta, status, setCountry } = useCountry();

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--paper)" }}
      data-testid="gift-page"
    >
      <div className="grain-overlay" aria-hidden="true" />

      {/* Top bar */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="gift-brand">
          <span
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-[8px]"
            style={{ background: "var(--ink)" }}
          >
            <span className="font-serif italic font-bold text-xl leading-none text-[color:var(--paper)]">
              B
            </span>
            <span
              className="absolute rounded-full"
              style={{
                width: "5px",
                height: "5px",
                top: "5px",
                right: "6px",
                background: "var(--accent-orange)",
              }}
            />
          </span>
          <span className="font-serif italic text-xl">Books for Little Hands</span>
        </Link>
        <Link to="/" className="btn-outline !py-2 !px-4 !text-[11px]">
          ← Home
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-8">
        <p className="folio-num">Gift Guide · Ages 4 — 10</p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="mt-4 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl"
        >
          The perfect gift for{" "}
          <em className="italic" style={{ color: "var(--accent-orange)" }}>
            curious kids.
          </em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.9 }}
          className="mt-6 max-w-2xl text-lg leading-relaxed text-stone-700"
        >
          Six illustrated picture books that turn the biggest ideas of a
          generation — money, banks, artificial intelligence — into a bedtime
          story, an activity, and a keepsake all at once. A birthday-worthy gift
          that gets re-read for years.
        </motion.p>

        {/* Country strip */}
        <div className="mt-8 flex items-center gap-3 flex-wrap text-[11px] tracking-[0.22em] uppercase">
          <span className="text-stone-500">
            {status === "loading" && "Detecting your country…"}
            {(status === "detected" || status === "manual" || status === "default") && meta && (
              <>
                Prices &amp; shipping via{" "}
                <span className="text-[color:var(--ink)] font-medium">
                  {meta.flag} {meta.store}
                </span>
              </>
            )}
            {status === "unknown" && "Choose your Amazon store:"}
          </span>
          <CountryPicker country={country} onChange={setCountry} />
        </div>
      </section>

      {/* Why it's a great gift */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-4">
            <p className="folio-num">Why gift it</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight">
              Four small reasons this becomes a big-deal gift.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                className="border-t border-stone-900/15 pt-6"
                data-testid={`gift-reason-${r.n}`}
              >
                <span className="folio-num">§ {r.n}</span>
                <h3 className="mt-2 font-serif text-2xl md:text-3xl leading-tight">
                  {r.title}
                </h3>
                <p className="mt-3 text-stone-700 leading-relaxed">{r.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundles */}
      <section
        id="bundles"
        className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16"
      >
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <p className="folio-num">Curated sets</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight">
              Ready-to-gift bundles.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {BUNDLES.map((b, i) => (
            <BundleCard
              key={b.id}
              bundle={b}
              country={country}
              delay={i * 0.05}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <p className="folio-num">Or explore individually</p>
        <h2 className="mt-3 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
          Browse every book.
        </h2>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/#books" className="btn-buy" data-testid="gift-cta-shop">
            Shop the bookshelf →
          </Link>
          <Link to="/about" className="btn-outline" data-testid="gift-cta-about">
            About us
          </Link>
        </div>
      </section>
    </div>
  );
}
